import { defineStore } from 'pinia';
import { cardMyList, pcCardLogList, unActiveCardNum } from '@/api/card';

function createTabState() {
  return {
    list: [],
    currentIndex: 0,
    records: [],
    loading: false
  };
}

function resolveListCardType(item = {}) {
  const candidates = [
    item?.cardType,
    item?.card_type
  ];

  for (const candidate of candidates) {
    const value = Number(candidate);
    if (value === 1 || value === 2) {
      return value;
    }
  }

  return null;
}

function filterCardsByType(list = [], cardType) {
  if (!Array.isArray(list)) {
    return [];
  }

  const normalizedType = Number(cardType || 0);
  const hasTypedCard = list.some((item) => resolveListCardType(item) !== null);
  if (!hasTypedCard) {
    return [...list];
  }

  return list.filter((item) => resolveListCardType(item) === normalizedType);
}

export const useCardStore = defineStore('card', {
  state: () => ({
    tabs: {
      1: createTabState(),
      2: createTabState()
    },
    unActiveCount: 0
  }),
  getters: {
    cardsByType: (state) => (cardType) => state.tabs[cardType]?.list || [],
    recordsByType: (state) => (cardType) => state.tabs[cardType]?.records || [],
    currentIndexByType: (state) => (cardType) => state.tabs[cardType]?.currentIndex || 0,
    currentCardByType: (state) => (cardType) => {
      const tab = state.tabs[cardType] || createTabState();
      return tab.list[tab.currentIndex] || {};
    },
    loadingByType: (state) => (cardType) => Boolean(state.tabs[cardType]?.loading)
  },
  actions: {
    ensureTab(cardType) {
      if (!this.tabs[cardType]) {
        this.tabs[cardType] = createTabState();
      }
      return this.tabs[cardType];
    },
    setCurrentIndex(cardType, index) {
      const tab = this.ensureTab(cardType);
      tab.currentIndex = index;
    },
    clearRecords(cardType) {
      const tab = this.ensureTab(cardType);
      tab.records = [];
    },
    async fetchCards({ address, cardType, deviceToken = '', selectedCardId = '' } = {}) {
      const tab = this.ensureTab(cardType);
      tab.loading = true;
      try {
        const currentCardId = String(
          selectedCardId
          || tab.list[tab.currentIndex]?.id
          || ''
        );
        const result = await cardMyList({
          address,
          cardType,
          deviceToken
        });
        tab.list = filterCardsByType(result?.list, cardType);
        if (!tab.list.length) {
          tab.currentIndex = 0;
          return tab.list;
        }
        const matchedIndex = currentCardId
          ? tab.list.findIndex((item) => String(item?.id || '') === currentCardId)
          : -1;
        tab.currentIndex = matchedIndex >= 0 ? matchedIndex : Math.min(tab.currentIndex, tab.list.length - 1);
        return tab.list;
      } finally {
        tab.loading = false;
      }
    },
    async fetchRecentRecords({ address, cardType, cardId, pageNum = 1, pageSize = 3, lastDays = '' } = {}, config = {}) {
      const tab = this.ensureTab(cardType);
      if (!cardId) {
        tab.records = [];
        return [];
      }
      const result = await pcCardLogList({
        address,
        cardId,
        pageNum,
        pageSize,
        lastDays
      }, config);
      tab.records = Array.isArray(result?.list) ? result.list : [];
      return tab.records;
    },
    async fetchUnActiveCount() {
      const result = await unActiveCardNum();
      this.unActiveCount = Number(result?.count || result || 0);
      return this.unActiveCount;
    }
  }
});
